import React, { useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import AnalyticsDeals from './components/AnalyticsDeals';
import EnterpriseNavigation from '../../components/EnterpriseNavigation';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import useMultiSort from 'hooks/useMultiSort';
import MultiSortPanel from 'components/MultiSortPanel';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import AnalyticsFitlers from '../Analytics/components/AnalyticsFitlers';
import {
  TABLE_COLUMNS,
  SORTING_VALUES,
  HEADING_TRANSLATIONS,
  LOCKED_COLUMN,
} from './components/AnalyticsDeals/components/data';
import {
  ASCENDING_DESCENDING_COPY,
  INITIAL_ADVANCED_COPY,
  NO_LISTING_WITH_LISTING_COPY,
} from 'models/Sort';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';
import EnterpriseContext from '../../providers/Enterprise/Enterprise.context';
import useDealsFilterOptions from '../../hooks/useDealsFilterOptions';
import useQueryParams from 'hooks/useQueryParams';
import { getHasActiveFilters } from 'utils';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import ManageColumns from 'components/ManageColumns';

import './Deals.styles.scss';

const MULTI_SORT_DIRECTION_COPY = {
  [SORTING_VALUES.stage]: INITIAL_ADVANCED_COPY,
  [SORTING_VALUES.listing]: NO_LISTING_WITH_LISTING_COPY,
  [SORTING_VALUES.buyersSort]: ASCENDING_DESCENDING_COPY,
  [SORTING_VALUES.filesSort]: ASCENDING_DESCENDING_COPY,
  [SORTING_VALUES.colleaguesCount]: ASCENDING_DESCENDING_COPY,
  [SORTING_VALUES.successFeeShare]: ASCENDING_DESCENDING_COPY,
};

type DealsProps = {
  className?: string;
};

const Deals: React.FC<DealsProps> = (props) => {
  const { className } = props;

  const [isEmpty, setIsEmpty] = useState(false);

  const classes = classNames('deals', className);

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const { multiSortValues, multiSortDirectionTranslation } = useMultiSort(
    TABLE_COLUMNS,
    SORTING_VALUES,
    HEADING_TRANSLATIONS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const { units } = useContext(EnterpriseContext);
  const { filterOptions } = useDealsFilterOptions(units);

  const { params } = useQueryParams();

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(filterOptions, params),
    [filterOptions, params],
  );

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onReorder,
    onColumnSizingChange,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_DEALS,
    TABLE_COLUMNS,
  );

  return (
    <div className={classes}>
      <div className="filter-row">
        <EnterpriseNavigation />
        <div className="filter-wrapper">
          {(!isEmpty || hasActiveFilters) && (
            <>
              {!isEmpty && (
                <NumberOfResultsPerPageFilter
                  limit={limit}
                  updateLimit={updateLimit}
                />
              )}
              <AnalyticsFitlers ref={multiFilterRef} />
              <MultiSortPanel
                ref={multiSortRef}
                options={multiSortValues}
                sortDirectionTranslations={multiSortDirectionTranslation}
              />
            </>
          )}
          <ManageColumns
            allColumns={allColumns}
            hiddenColumns={hiddenColumns}
            headingTranslations={HEADING_TRANSLATIONS}
            lockedColumn={LOCKED_COLUMN}
            isDefaultColumns={isDefaultColumns}
            onSave={saveColumnSettings}
            onResetToDefault={resetToDefault}
          />
        </div>
      </div>
      <AnalyticsDeals
        translatedSortOptions={multiSortValues}
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
        setIsEmpty={setIsEmpty}
        columns={columns}
        columnSizing={columnSizing}
        onReorder={onReorder}
        onColumnSizingChange={onColumnSizingChange}
      />
    </div>
  );
};

export default Deals;
